import React, { useState, useRef, useEffect } from 'react';

import { Typography, Chip, Button } from '@material-ui/core';
import CancelIcon from '@material-ui/icons/Cancel';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import RemoveCircleOutlineIcon from '@material-ui/icons/RemoveCircleOutline';
import VisibilityIcon from '@material-ui/icons/Visibility';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';
import { useTranslation } from 'react-i18next';

import RichTextContent from 'components/RichTextContent/RichTextContent';
import type { ActionLibraryInstanceStep } from 'config/api/actionLibraryInstance/actionLibraryInstance.types';
import general_messages from 'messages/general_messages';

import useStyles from './ActionLibraryInstanceDetailsSteps.styles';

type StepItemProps = {
  step: ActionLibraryInstanceStep;
  index: number;
};

const StepItem: React.FC<StepItemProps> = ({ step, index }) => {
  const { t } = useTranslation();
  const styles = useStyles();
  const [expanded, setExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = contentRef.current;
    if (element) {
      const checkOverflow = () => {
        if (!expanded) {
          setIsOverflowing(element.scrollHeight > element.clientHeight);
        }
      };

      checkOverflow();
      window.addEventListener('resize', checkOverflow);
      return () => {
        window.removeEventListener('resize', checkOverflow);
      };
    }
    return () => {};
  }, [expanded, step.instruction]);

  // Determine status icon and color
  const getStatusDisplay = () => {
    const status = step.status || (step.isCompleted ? 'completed' : 'not_started');

    if (status === 'completed') {
      return {
        Icon: CheckCircleIcon,
        color: styles.completedColor,
        label: step.statusDisplay || 'Completed',
      };
    }
    if (status === 'in_progress') {
      return {
        Icon: RemoveCircleOutlineIcon,
        color: styles.inProgressColor,
        label: step.statusDisplay || 'In Progress',
      };
    }
    return {
      Icon: CancelIcon,
      color: styles.notStartedColor,
      label: step.statusDisplay || 'Not Started',
    };
  };

  const statusDisplay = getStatusDisplay();
  const StatusIcon = statusDisplay.Icon;

  return (
    <div id={`step-${step.id}`} style={{ marginBottom: 16, padding: 16, border: '1px solid #e0e0e0', borderRadius: 4 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <Typography style={{ display: 'flex', alignItems: 'center', gap: 8 }} variant='h6'>
          {index + 1}. {step.title}
          {step.mandatory && <Chip className={styles.mandatoryChip} label='Mandatory' size='small' variant='outlined' />}
        </Typography>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <StatusIcon className={statusDisplay.color} style={{ fontSize: '1.2rem' }} />
          <Typography className={statusDisplay.color} variant='body2'>
            {statusDisplay.label}
          </Typography>
        </div>
      </div>
      <Typography component='div' style={{ marginBottom: 8 }} variant='body2'>
        <div ref={contentRef} className={!expanded ? styles.clampedText : ''}>
          <RichTextContent content={step.instruction} />
        </div>
        {(isOverflowing || expanded) && (
          <div className={styles.readMoreContainer}>
            <Button
              className={styles.readMoreButton}
              onClick={() => setExpanded(!expanded)}
              size='small'
              startIcon={expanded ? <VisibilityOffIcon /> : <VisibilityIcon />}
            >
              {expanded ? t(general_messages.hide) : t(general_messages.show_more)}
            </Button>
          </div>
        )}
      </Typography>
      {step.blockReason && (
        <Typography color='error' style={{ marginTop: 8 }} variant='body2'>
          <strong>Block Reason:</strong> {step.blockReason}
        </Typography>
      )}
      {step.completionComment && (
        <Typography color='textSecondary' style={{ marginTop: 8 }} variant='body2'>
          <strong>Completion Comment:</strong> {step.completionComment}
        </Typography>
      )}
    </div>
  );
};

type Props = {
  steps?: ActionLibraryInstanceStep[];
};

const ActionLibraryInstanceDetailsSteps: React.FC<Props> = ({ steps }) => {
  if (!steps || steps.length === 0) return null;

  return (
    <>
      {steps
        .sort((a, b) => (a.order || 0) - (b.order || 0))
        .map((step, index) => (
          <StepItem key={step.id} index={index} step={step} />
        ))}
    </>
  );
};

export default ActionLibraryInstanceDetailsSteps;
